import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, X } from 'lucide-react';
import AppSelect from 'src/components/common/AppSelect';
import { Button } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';

/**
 * Chip-based multi-select with a Mandatory / Optional flag per item — mirrors
 * the requirement template's skills / certifications UI.
 *
 * value: array of { value: string, isMandatory: boolean }
 * options: array of { value, label }
 */
export const TagSelector = ({
  label,
  options,
  value,
  onChange,
  placeholder,
}) => {
  const items = value || [];
  const [draftValue, setDraftValue] = useState('');
  const [draftMandatory, setDraftMandatory] = useState(true);

  const labelFor = (val) =>
    options.find((o) => String(o.value) === String(val))?.label ?? val;

  // Don't offer values already added.
  const availableOptions = options.filter(
    (o) => !items.some((it) => String(it.value) === String(o.value))
  );

  const add = () => {
    if (!draftValue) return;
    if (items.some((it) => String(it.value) === String(draftValue))) return;
    onChange([...items, { value: draftValue, isMandatory: draftMandatory }]);
    setDraftValue('');
    setDraftMandatory(true);
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div>
      {label && (
        <span className="block text-sm font-semibold leading-6 text-foreground-strong mb-1.5">
          {label}
        </span>
      )}

      <div className="flex items-stretch gap-2 flex-wrap">
        <AppSelect
          options={availableOptions}
          value={draftValue}
          onValueChange={setDraftValue}
          placeholder={placeholder}
          className="flex-1 min-w-[180px]"
        />
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setDraftMandatory(true)}
            className={cn(
              'px-3 py-1.5 text-[11px] font-medium transition-colors',
              draftMandatory
                ? 'bg-brand-bg-subtle text-primary'
                : 'bg-card text-muted-foreground'
            )}
          >
            Mandatory
          </button>
          <button
            type="button"
            onClick={() => setDraftMandatory(false)}
            className={cn(
              'px-3 py-1.5 text-[11px] font-medium transition-colors',
              !draftMandatory
                ? 'bg-muted text-foreground-strong'
                : 'bg-card text-muted-foreground'
            )}
          >
            Optional
          </button>
        </div>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={add}
          disabled={!draftValue}
          className="h-9"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>

      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {items.map((it, idx) => (
            <span
              key={it.value}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-medium border',
                it.isMandatory
                  ? 'bg-brand-bg-subtle text-primary border-primary/15'
                  : 'bg-muted text-foreground-strong border-border'
              )}
            >
              {labelFor(it.value)}
              <span
                className={cn(
                  'text-[9.5px] font-semibold px-1.5 py-px rounded tracking-[0.03em]',
                  it.isMandatory
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-border text-muted-foreground'
                )}
              >
                {it.isMandatory ? 'REQ' : 'OPT'}
              </span>
              <button
                type="button"
                onClick={() => remove(idx)}
                title="Remove"
                className="text-muted-foreground hover:text-ds-danger"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

TagSelector.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  value: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

TagSelector.defaultProps = {
  label: '',
  options: [],
  value: [],
  placeholder: 'Select',
};

export default TagSelector;
